import * as ko from "knockout";
import * as R from "ramda";
import { isBrowser } from "targetenv";

import { GridViewModel, PliKoGridProperties, PliKoGridViewModel } from "../ko-grid/knockoutGridViewModel";
import { ItemFilterBuilder, defaultItemFilterBuilder } from "../ko-grid/itemFilter";
import { isEmpty, KeyProvider } from "../util/array";
import { CompositeDisposable, disposeObject } from "../util/disposable";
import { ShortcutMap } from "../util/keyMap";
import { isNullOrWhitespace } from "../util/string";

import { BatchEditWrapper, RecordBase, ViewModelBase, forEachWriteableField, setItemError } from "./shared";

/** TODO: Remove in web-pli v8. */
export { RecordBase };


/**
 * Default edit grid settings.
 */
const defaultSettings: EditGridSettings<any> = {
    disabled: false,
    multiSelect: true,
    rowCssProvider: defaultRowCssProvider
};

/** Classes returned when a row has errors. */
const errorClasses: string[] = ["error"];

/** Classes returned when a row is new. */
const newClasses: string[] = ["new"];

/** Classes returned when a row is neither new nor empty. */
const noClasses: string[] = [];

/** Regex to extract item index and property name for setErrorMessage. */
const indexPropertyMatcher = /^(items)?\[(\d+)\].(\S+)$/;

/**
 * The default row css provider.
 *
 * @param row
 *     Current row item.
 * @returns
 *     Array of css classes to be applied to the current row (["error"] if item has errors,
 *     ["new"] if item is new, or []).
 */
function defaultRowCssProvider<TRecord extends RecordBase>(row: TRecord): string[] {
    return (rowHasErrors(row)
        ? errorClasses
        : (!!row.isNewRecord ? newClasses : noClasses));
}

/**
 * Returns true if a row has errors.
 * Inspects "extras.errorsCount" and "_errors", in that order.
 */
export function rowHasErrors(row: any): boolean {
    const extras = row.extras;
    const errors = row._errors;
    return (extras && ko.isObservable(extras.errorsCount) && !!extras.errorsCount.peek())
        || (typeof errors === "object" && !isEmpty(errors));
}

/**
 * Possible behaviours for committing pending changes.
 */
export enum CommitMode {
    /**
     * Auto-commit when editing a single item, manual commit when editing multiple items (default mode).
     */
    Mixed,

    /**
     * Auto-commit regardless of number of items being edited.
     */
    Auto,

    /**
     * Manual commit regardless of number of items being edited.
     */
    Manual
}

/**
 * Construction settings for EditGrid.
 */
export interface EditGridConstructorSettings<TViewModel extends ViewModelBase, TRecord extends RecordBase> extends EditGridSettings<TRecord> {
    /**
     * Defines function that will be used to create a view item (bound to edit panel).
     *
     * Either "createViewItem" or "viewItemClass" must be defined;
     * "viewItemClass" has higher precedence if both are defined.
     *
     * @param items
     *     Current selected grid items.
     */
    createViewItem?: (items?: TRecord[]) => TViewModel;

    /**
     * EditGrid identifier; used for keymap activation.
     */
    id: string;

    /**
     * If defined, provides a key to sort the public "items" property by.
     *
     * This will ensure that the items are exposed in a consistent sort order,
     * preventing the following series of actions from setting the dirty state
     * of a viewmodel:
     *
     * 1) Edit a value.
     * 2) Sort the grid.
     * 3) Revert edit in step 1.
     */
    sortKeyProvider?: KeyProvider<TRecord>;

    /**
     * Defines type that will be used to create a view item (bound to edit panel).
     *
     * Either "createViewItem" or "viewItemClass" must be defined;
     * "viewItemClass" has higher precedence if both are defined.
     */
    viewItemClass?: { new(items?: TRecord[]): TViewModel };
}

/**
 * Settings controlling the behaviour of EditGrid.
 */
export interface EditGridSettings<TRecord extends RecordBase> extends PliKoGridProperties<TRecord> {
    /**
     * Activates/deactivates the edit grid keybindings when true/false.
     */
    active?: boolean;

    /**
     * Commit behaviour mode.
     * @default CommitMode.Mixed
     */
    commitMode?: CommitMode;

    /**
     * Set to 'true' to require a row to be selected/checked before it can be edited; 'false' allows editing when just focused.
     * @default false
     */
    editRequiresSelection?: boolean;

    /**
     * Filter text to apply to the ItemFilter.
     */
    filter?: string;

    /**
     * Columns to include when filtering.
     * @default include all columns.
     */
    filterColumns?: string[];

    /**
     * Function that generates an ItemFilter function for each filter value.
     * @default Filters with 'searchquery' and prefix matching.
     */
    itemFilterBuilder?: ItemFilterBuilder;

    /**
     * Function that will be called when changes to a set of items are committed.
     * @param items
     *     Items that were committed.
     */
    onItemsCommitted?: (items: TRecord[]) => void;
}

/**
 * Pure computed container for an array of item records with setErrorMessage support.
 */
export interface ComputedRecordArray<TRecord extends RecordBase> extends ko.PureComputed<TRecord[]> {
    /**
     * Sets error message for the item at the given path.
     *
     * Expects path to be formatted like "[42].someProperty".
     *
     * @message
     *     Error message to set for the given item/property.
     * @path
     *     Path to the item/property to which the error message applies.
     */
    setErrorMessage(message: string, path: string): void;
}

/** Settings keys unique to EditGrid (used in EditGrid::apply()) */
const editGridSettingsKeys = [
    "active",
    "commitMode",
    "createViewItem",
    "editRequiresSelection",
    "filter",
    "filterColumns",
    "itemFilterBuilder",
    "onItemsCommitted",
    "viewItemClass"
];

/**
 * Bridges a ko-grid to an item edit panel, allowing for fast, keyboard-driven editing.
 */
export class EditGrid<TViewModel, TRecord extends RecordBase> {

    /**
     * Creates a new EditGrid instance.
     *
     * @param settings
     *     Initial settings to apply. "createViewItem" or "viewItemClass" is required.
     */
    constructor(settings: EditGridConstructorSettings<TViewModel, TRecord>) {
        if (typeof settings.id !== "string") { throw new Error(`"settings.id" is required`); }
        if (!settings.createViewItem && !settings.viewItemClass) { throw new Error(`Either "settings.viewItemClass" or "settings.createViewItem" is required`); }

        this._active = ko.observable(false);
        this._commitMode = CommitMode.Mixed;
        this._customActions = {};
        this._editRequiresSelection = false;
        this._grid = new GridViewModel<TRecord>();
        this._idField = "id";
        this._itemFilterBuilder = defaultItemFilterBuilder;
        this._items = ko.observable([]);
        this._viewItemClass = settings.viewItemClass;
        this._viewItemFactory = settings.createViewItem;
        this._viewItem = ko.observable(this._createViewItem([]));
        this._filterColumns = Object.keys(this._viewItem());

        const itemsSorter = (settings.sortKeyProvider
            ? R.sort((a: TRecord, b: TRecord) => {
                const propertyA = settings.sortKeyProvider(a);
                const propertyB = settings.sortKeyProvider(b);
                return propertyA > propertyB ? 1 : (propertyA < propertyB ? -1 : 0);
            })
            : R.identity);

        this.active = ko.pureComputed<boolean>(this._active, this);
        this.commitConfirmMessage = ko.pureComputed(this._computeCommitConfirmMessage, this);
        this.currentItems = ko.pureComputed<TRecord[]>(this._currentItems, this);
        this.currentItemsCount = ko.pureComputed(() => this._currentItems().length, this);
        this.dirty = ko.pureComputed<boolean>(this._dirty, this);
        this.errorsCount = ko.pureComputed(() => this._items().filter(rowHasErrors).length);
        this.filteredItems = ko.pureComputed(() => this._grid.items() || []);
        this.focusedItem = ko.pureComputed(() => this._grid.focusedItem());
        this.keyMap = this._combineDefaultShortcutsWith({});
        this.id = settings.id;
        this.items = ko.pureComputed<TRecord[]>(() => itemsSorter(this._items())) as ComputedRecordArray<TRecord>;
        this.items.setErrorMessage = this.setErrorMessage.bind(this);
        this.readOnly = ko.pureComputed(() => this._editRequiresSelection && this.selectedItems().length === 0, this);
        this.selectedItems = ko.pureComputed(() => this._grid.selectedItems() || [], this);
        this.viewItem = ko.pureComputed<TViewModel>(this._viewItem, this);

        this._byId = this._byId.bind(this);
        this.commit = this.commit.bind(this);
        this.reject = this.reject.bind(this);

        this._disposables = new CompositeDisposable(
            this._grid.focusedItem.subscribe(this._syncFocusedItem, this),
            this._grid.selectedItems.subscribe(this._syncFocusedItem, this)
        );

        this.apply(R.merge(defaultSettings, settings));
    }


    // -------------------------------------------------------------------------
    // Viewmodel properties
    // -------------------------------------------------------------------------

    /** Returns true when the EditGrid is active. */
    active: ko.PureComputed<boolean>;

    /** Message displayed in commit confirmation panel. */
    commitConfirmMessage: ko.PureComputed<string>;

    /** Items that are currently being edited. */
    currentItems: ko.PureComputed<TRecord[]>;

    /** Number of items currently being edited. */
    currentItemsCount: ko.PureComputed<number>;

    /** Returns true when there are pending changes to the current items. */
    dirty: ko.PureComputed<boolean>;

    /** Returns the number of rows that are in an error state. */
    errorsCount: ko.PureComputed<number>;

    /** Returns list of items after the filter has been applied. */
    filteredItems: ko.PureComputed<TRecord[]>;

    /** Returns the currently focused item. */
    focusedItem: ko.PureComputed<TRecord>;

    /** EditGrid identifier. */
    id: string;

    /** Returns list of all items. */
    items: ComputedRecordArray<TRecord>;

    /** Keymap to activate when the EditGrid is active. */
    keyMap: ShortcutMap<Function>;

    /** Returns true when the view item is read-only. */
    readOnly: ko.PureComputed<boolean>;

    /** Items that are currently selected (checked). Always returns an array value. */
    selectedItems: ko.PureComputed<TRecord[]>;

    /** Item template/proxy bound to the edit panel. */
    viewItem: ko.PureComputed<TViewModel>;


    // -------------------------------------------------------------------------
    // Public actions
    // -------------------------------------------------------------------------

    /**
     * Adds a new item to the grid and adjusts grid state accordingly.
     * Only allows a single "new" item to be in the array. If the last item is
     * a "new" item, it will simply focus that row.
     *
     * @param item
     *     Item to append to the grid.
     */
    addItem(item: TRecord): void {
        const items = this._items.peek();
        if (items.length > 0 && items[items.length - 1].isNewRecord) {
            this.apply({ focusedItem: items[items.length - 1], invalidate: true });
        } else {
            item.isNewRecord = true;

            let newSelectedItems: TRecord[];
            if (this._editRequiresSelection) {
                newSelectedItems = [item];
            } else {
                newSelectedItems = this.selectedItems.peek().length ? [] : undefined;
            }

            this._deferValidate = true;

            items.push(item);
            this.apply({
                filter: "",
                focusedItem: item,
                invalidate: true,
                items: items,
                selectedItems: newSelectedItems
            });
        }
    }

    /**
     * Applies specified settings to the grid.
     *
     * @param settings
     *     EditGridSettings to apply. Any settings that are "undefined" are ignored.
     */
    apply(settings: EditGridSettings<TRecord>): void {
        const properties: PliKoGridProperties<TRecord> = R.omit(editGridSettingsKeys, settings);

        // 1. Initialize properties
        if (settings.commitMode !== undefined) { this._commitMode = settings.commitMode; }
        if (settings.editRequiresSelection !== undefined) { this._editRequiresSelection = !!settings.editRequiresSelection; }
        if (settings.filter !== undefined) { this._filter = settings.filter; }
        if (settings.filterColumns !== undefined) { this._filterColumns = settings.filterColumns; }
        if (settings.idField !== undefined) {
            this._idField = settings.idField;
            properties.idField = settings.idField;
        }
        if (settings.itemFilterBuilder !== undefined) { this._itemFilterBuilder = settings.itemFilterBuilder; }
        if (settings.items !== undefined) { properties.items = settings.items; }
        if (settings.onItemsCommitted !== undefined) { this._onItemsCommitted = settings.onItemsCommitted; }
        if (settings.shortcutActions !== undefined) {
            this.keyMap = this._combineDefaultShortcutsWith(settings.shortcutActions || {});

            // set every action to 'false' for the keys defined in keymap
            // to prevent the default SlickGrid action for that key
            const disabledActions = R.map(() => false, this.keyMap);
            properties.shortcutActions = disabledActions;
        }


        // 2. Set dependent properties
        if (settings.active === true && settings.focusedItem === undefined) {
            if (!this._grid.focusedItem()) {
                properties.focusedItem = this._items.peek()[0];
            }
        }
        if (settings.filter !== undefined || settings.items !== undefined) {
            if (properties.items === undefined) {
                properties.items = this._items.peek();
            }
            if (this._filter.length > 0) {
                const itemFilter = this._itemFilterBuilder(properties.items, this._filterColumns);
                // TODO: support real async item filters!
                itemFilter(this._filter, (items: TRecord[]) => { properties.items = items; });
            }
        }
        if (properties.items !== undefined) {
            if (properties.items.length > 0) {
                // Pick new focused item if necessary
                if (properties.focusedItem === undefined) {
                    const focusedItem = this._grid.focusedItem() as any;
                    if (!focusedItem || !properties.items.some((item: any) => item[this._idField] === focusedItem[this._idField])) {
                        properties.focusedItem = properties.items[0];
                    }
                }
                // Adjust selected items
                if (properties.selectedItems === undefined) {
                    const selectedItems = this.selectedItems();
                    if (selectedItems.length > 0) {
                        const newSelectedItems = R.intersectionWith(this._byId, selectedItems, properties.items);
                        if (R.differenceWith(this._byId, selectedItems, newSelectedItems).length > 0) {
                            properties.selectedItems  = newSelectedItems;
                        }
                    }
                }
            } else {
                properties.focusedItem = null;
                properties.selectedItems = [];
            }
        }


        // 3. Update ko-grid instance
        if (settings.active === true && !this._active.peek()) {
            // 'resize' will completely redraw the grid, which may be necessary if
            // it was bound when the container element was hidden. SlickGrid
            // relies on having actual container dimensions for rendering.
            // This must be done in a separate call to 'apply', otherwise changes
            // in focus/selection may be obscured by an incorrect scroll position.
            this._grid.apply({ resize: true });
        }
        if (!isEmpty(properties)) {
            this._grid.apply(properties);
        }


        // 4. Update affected observables
        if (settings.active !== undefined) { this._active(settings.active); }
        if (settings.items !== undefined) { this._items(settings.items); }
        if (properties.items !== undefined || properties.focusedItem !== undefined || properties.selectedItems !== undefined) {
            // The edit wrapper might need to be updated
            this._syncFocusedItem();
        }
    }

    /**
     * Commits any pending changes to the current items.
     */
    commit(): boolean {
        if (this._editWrapper) {
            this._editWrapper.commit();
            return false;
        }
        return true;
    }

    /**
     * Deletes items from the grid and sets focus appropriately.
     *
     * @param itemsToDelete
     *     Items to remove from the grid.
     */
    deleteItems(itemsToDelete: TRecord[]): void {
        const oldItems: any[] = this._items.peek();
        const items = R.differenceWith(this._byId, oldItems, itemsToDelete);

        const idsToDelete: string[] = itemsToDelete.map((item: any) => item[this._idField]);
        const focusedItemId: string = (this.focusedItem.peek() as any || {})[this._idField];
        let focusedItemIndex = focusedItemId
            ? R.findIndex(item => item[this._idField] === focusedItemId, oldItems)
            : 0;

        // find the first non-deleted item before the previously focused item
        while (focusedItemIndex >= 0 && idsToDelete.indexOf(oldItems[focusedItemIndex][this._idField]) >= 0) {
            focusedItemIndex--;
        }

        const focusedItem = focusedItemIndex === -1 ? items[0] : oldItems[focusedItemIndex];

        this.apply({ items, focusedItem });
    }

    /**
     * Disposes any resources/subscriptions used by the EditGrid.
     */
    dispose(): void {
        if (this._editWrapper) {
            this._editWrapper.dispose();
        }
        this._disposables.dispose();
        disposeObject(this.viewItem(), true);
    }

    /**
     * Rejects any pending changes on the current items.
     */
    reject(): boolean {
        if (this._editWrapper) {
            this._editWrapper.reject();
            return false;
        }
        return true;
    }

    /**
     * Sets the error message for the item at the given path and applies it to the grid.
     *
     * This is not intended to be called directly. Rather, it will be
     * called when a view model that contains an EditGrid instance
     * receives server-side validation results from a "Save" call.
     *
     * @param message
     *     Error message to set.
     * @param path
     *     Path to the target item/property.
     */
    setErrorMessage(message: string, path: string): void {
        const matches = indexPropertyMatcher.exec(path);
        if (matches !== null) {
            const index = parseInt(matches[2], 10);
            const items = this.items();
            const item = items[index];
            if (item) {
                const key = matches[3];
                setItemError(item, key, message);
                // TODO: queue item errors and apply one time?
                this.apply({ items });
                return;
            }
        }

        // unable to apply
        if (isBrowser && console.warn) {
            console.warn(`EditGrid ${this.id}: unable to set error message for path "${path}"`);
        }
    }


    // -------------------------------------------------------------------------
    // Internal actions
    // -------------------------------------------------------------------------

    /**
     * Registers a named action that can be bound to a shortcut key.
     * @param name
     *     Action name.
     * @param action
     *     Function to execute when action invoked.
     * @param context
     *     Value to use for 'this' when invoking action.
     */
    _registerAction(name: string, action: Function, context?: any): void {
        if (isNullOrWhitespace(name)) { throw new Error("Expected \"name\" to contain an action name."); }
        if (typeof action !== "function") { throw new Error("Expected \"action\" to be a function."); }
        this._customActions[name.toLowerCase()] = [action, context];
    }


    // -------------------------------------------------------------------------
    // Private
    // -------------------------------------------------------------------------

    /**
     * Predicate that compares by the value in "this._idField".
     */
    private _byId(a: any, b: any): boolean {
        return a[this._idField] === b[this._idField];
    }

    /**
     * Gets the text for the Commit Confirmation panel.
     */
    private _computeCommitConfirmMessage(): string {
        const count = this.currentItemsCount();
        return `Confirm edits (${count} ${count === 1 ? "row" : "rows"})?`;
    }

    /**
     * Creates a viewItem and sets updateDelay to 0 on any pliTextBox fields.
     */
    private _createViewItem(items: TRecord[]): TViewModel {
        const viewItem = this._viewItemClass
            ? new this._viewItemClass(items)
            : this._viewItemFactory(items);

        // Set updateDelay=0 on any pliTextBox fields to ensure
        // that value updates are propagated immediately. This is
        // necessary because the default update delay is 500ms;
        // when speed-editing a single field, this may cause updates
        // to be missed because the field focus will not change and
        // the row might be switched long before the 500ms timeout.
        forEachWriteableField(viewItem, (key: string, o: any) => {
            if (typeof o.load === "function") {
                if (!!o.behaviourData && typeof o.behaviourData === "string") {
                    o.behaviourData = { format: o.behaviourData };
                }
                else if (!!o.behaviourData && typeof o.behaviourData !== "object" && typeof o.behaviourData !== "function") {
                    throw new Error(`Unsupported behaviour data for property "${key}". ` +
                    `The edit grid supports an "object", "function" or "null" but got "${typeof o.behaviourData}".`);
                }
                if (!o.behaviourData) {
                    o.behaviourData = {};
                }
                if (typeof o.behaviourData.updateDelay !== "number") {
                    o.behaviourData.updateDelay = 0;
                }
            }
        });

        return viewItem;
    }

    /**
     * Construct a new EditWrapper each time focus or selection changes.
     */
    private _initEditWrapper(items: TRecord[]): void {
        if (this._editWrapper) {
            // if items haven't changed, don't do anything to the edit wrapper
            // this most often occurs when deselecting a single item
            if (R.equals(items, this._editWrapper.items)) {
                // if there is a change in selection state and editing requires selection,
                // reject any pending changes
                if (this._dirty() && this._editRequiresSelection) {
                    this.reject();
                }
                // ensure view item's validation state is in sync with
                // errors for the currently selected item(s)
                this._editWrapper.updateErrors();
                return;
            }

            this._editWrapper.dispose();
            this._dirtySubscription.dispose();
        }

        // only update the view item if it has a different shape for this set of items
        const currentViewItem = this.viewItem.peek();
        const newViewItem = this._createViewItem(items);
        if (!R.equals(Object.keys(currentViewItem), Object.keys(newViewItem))) {
            this._viewItem(newViewItem);
        }

        const editWrapper = new BatchEditWrapper(items, this.viewItem(), this.readOnly, this._deferValidate);
        this._currentItems(editWrapper.items);
        this._editWrapper = editWrapper;
        this._deferValidate = false;
        this._dirtySubscription = editWrapper.dirty.subscribe(this._onEditWrapperDirtyChanged, this);
    }

    /**
     * Handles changes to 'dirty' state on the edit wrapper.
     */
    private _onEditWrapperDirtyChanged(dirty: boolean): void {
        if (dirty) {
            if (this._commitMode === CommitMode.Auto || (this._commitMode === CommitMode.Mixed && this.currentItemsCount() === 1)) {
                this._editWrapper.commit();
            } else {
                this._dirty(true);
            }
        } else {
            if (typeof this._onItemsCommitted === "function") {
                this._onItemsCommitted(this._currentItems());
            }
            // only invalidate if dirty state reverts to 'false' because
            // this indicates either a commit or a reject operation
            this._grid.apply({ invalidate: true });
            // ensure any _items subscriptions are notified (e.g., errorsCount)
            this._items.valueHasMutated();
            this._dirty(false);
        }
    }

    /**
     * Returns a function that invokes either a custom action or a named grid action.
     */
    private _performGridAction(action: string, ...args: any[]): Function {
        const viewModel = this;
        return function actionInvoker(): boolean {
            const customActionEntry = viewModel._customActions[action.toLowerCase()];
            if (customActionEntry) {
                const customAction = customActionEntry[0];
                const context = customActionEntry[1];
                return customAction.apply(context, args.concat(Array.prototype.slice.call(arguments)));
            }

            viewModel._grid.apply({ action: action });
            return false;
        };
    }

    /**
     * Checks or unchecks all rows.
     */
    private _selectAllOrNone(): boolean {
        if (this.selectedItems().length < this._grid.items().length) {
            this._grid.apply({ action: "SelectAll" });
        } else {
            this._grid.apply({ action: "SelectNone" });
        }
        return false;
    }

    /**
     * Moves the selected item "down" by 1 or shifts the focus down in single select mode.
     */
    private _shiftSelectionNext(): boolean {
        if (!this.dirty()) {
            const selectedItems = this.selectedItems();
            if (selectedItems.length === 1) {
                this._grid.apply({ focusedItem: selectedItems[0] });
                this._grid.apply({ action: "SelectNone" });
                this._grid.apply({ action: "MoveNext" });
                this._grid.apply({ action: "Select" });
            } else if (selectedItems.length === 0) {
                this._grid.apply({ action: "MoveNext" });
            }
        }
        return false;
    }

    /**
     * Moves the selected item "up" by 1 or shifts the focus up in single select mode.
     */
    private _shiftSelectionPrevious(): boolean {
        if (!this.dirty()) {
            const selectedItems = this.selectedItems();
            if (selectedItems.length === 1) {
                this._grid.apply({ focusedItem: selectedItems[0] });
                this._grid.apply({ action: "SelectNone" });
                this._grid.apply({ action: "MovePrevious" });
                this._grid.apply({ action: "Select" });
            } else if (selectedItems.length === 0) {
                this._grid.apply({ action: "MovePrevious" });
            }
        }
        return false;
    }

    /**
     * Merges selectedItems/focusedItems update events and initializes the edit wrapper accordingly.
     */
    private _syncFocusedItem(): void {
        const selectedItems = this._grid.selectedItems();
        const focusedItem = this._grid.focusedItem();

        if ((!selectedItems || selectedItems.length === 0) && (!focusedItem || this.filteredItems().length === 0)) {
            this._initEditWrapper([]);
        } else {
            const items = (selectedItems && selectedItems.length > 0
                ? selectedItems
                : [focusedItem]);
            this._initEditWrapper(items);
        }
    }

    /** Controls whether the EditGrid is active or inactive. */
    private _active: ko.Observable<boolean>;

    /** Whether pending changes to multiple rows will be committed without user intervention. */
    private _commitMode: CommitMode;

    /** Stores the items that are currently being edited. */
    private _currentItems: ko.Observable<TRecord[]> = ko.observable([]);

    /** Custom actions that can be registered and bound to shortcut keys. */
    private _customActions: { [key: string]: {0: Function, 1: any} };

    /** Instructs EditWrapper to defer validation if, e.g., the current item is newly created. */
    private _deferValidate: boolean = false;

    /** Stores the dirty state of the current items. */
    private _dirty: ko.Observable<boolean> = ko.observable(false);

    /** Subscription to EditWrapper dirty observable. */
    private _dirtySubscription: ko.subscription<boolean>;

    /** Resources/subscriptions that should be cleaned up when EditGrid is disposed. */
    private _disposables: CompositeDisposable;

    /** Whether selection is required to edit a row. */
    private _editRequiresSelection: boolean;

    /** Proxies edit panel updates to all selected items. */
    private _editWrapper: BatchEditWrapper<TViewModel, TRecord>;

    /** Active filter string. */
    private _filter: string = "";

    /** Columns to include when filtering. */
    private _filterColumns: string[];

    /** Knockout grid instance. */
    private _grid: PliKoGridViewModel<TRecord>;

    /** Item identifier field. */
    private _idField: string | number;

    /** Function that generates an item filter function. */
    private _itemFilterBuilder: ItemFilterBuilder;

    /** All grid items. */
    private _items: ko.Observable<TRecord[]>;

    /** Item commit callback function. */
    private _onItemsCommitted: (items: TRecord[]) => void = null;

    /** Item template/proxy bound to the edit panel. */
    private _viewItem: ko.Observable<TViewModel>;

    /** Class to use for 'this.viewItem'. */
    private _viewItemClass: { new(items: TRecord[]): TViewModel };

    /** Function used when setting 'this.viewItem'. */
    private _viewItemFactory: (items: TRecord[]) => TViewModel;

    /** Combines user-defined shortcuts with a list of defaults. */
    private _combineDefaultShortcutsWith: (map: ShortcutMap<Function>) => ShortcutMap<Function> = R.merge({
        "ctrl+1": this._performGridAction("FocusField", 0),
        "ctrl+2": this._performGridAction("FocusField", 1),
        "ctrl+3": this._performGridAction("FocusField", 2),
        "ctrl+4": this._performGridAction("FocusField", 3),
        "ctrl+5": this._performGridAction("FocusField", 4),
        "ctrl+6": this._performGridAction("FocusField", 5),
        "ctrl+7": this._performGridAction("FocusField", 6),
        "ctrl+8": this._performGridAction("FocusField", 7),
        "ctrl+9": this._performGridAction("FocusField", 8),
        "ctrl+0": this._performGridAction("FocusField", 9),
        "ctrl+down": this._performGridAction("MoveNextAndFocus"),
        "ctrl+end": this._performGridAction("MoveEnd"),
        "ctrl+enter": (): boolean => this.commit(),
        "ctrl+home": this._performGridAction("MoveHome"),
        "ctrl+shift+down": this._performGridAction("SelectNext"),
        "ctrl+shift+end": this._performGridAction("SelectEnd"),
        "ctrl+shift+home": this._performGridAction("SelectHome"),
        "ctrl+shift+space,ctrl+shift+a": (): boolean => this._selectAllOrNone(),
        "ctrl+shift+up": this._performGridAction("SelectPrevious"),
        "ctrl+space": this._performGridAction("Select"),
        "ctrl+up": this._performGridAction("MovePreviousAndFocus"),
        "ctrl+[": (): boolean => this._shiftSelectionPrevious(),
        "ctrl+]": (): boolean => this._shiftSelectionNext(),
        "down": this._performGridAction("MoveNextAndFocus"),
        "enter": this._performGridAction("FocusNext"),
        "esc": (): boolean => this.reject(),
        "pagedown": this._performGridAction("PageDown"),
        "pageup": this._performGridAction("PageUp"),
        "shift+tab": null,
        "tab": null,
        "up": this._performGridAction("MovePreviousAndFocus")
    });

}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/ko-editgrid/editGrid.ts